import { combineLatest, Observable, Subscription } from 'rxjs';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { KeyHandlerService } from '../../services/keyhandler/keyhandler.service';
import { CanvasService } from '../../services/canvas/canvas.service';
import { Point } from '../../elements/base/point';
import { ComponentEvent } from '../../components/general-component/general.component.type';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { FileUploaderComponent } from '../../components/file-uploader/file-uploader.component';
import { ActivatedRoute } from '@angular/router';
import {
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  tap,
} from 'rxjs/operators';
import { HttpService } from '../../store/http/http.service';
import { Store } from '@ngrx/store';
import {
  loadProjectWithFilesById,
  setCurrentFileID,
  setCurrentProject,
  setProjectLoaded,
} from '../../projects/project.actions';
import { bulkUpdateFinish } from '../store/editor.actions';
import {
  setDescriptorValue,
  incrementStrokeWidth,
} from '../store/editor.crud.actions';
import { ShapeService } from '../shape/shape.service';
import {
  OverlayComponentType,
  OverlayConfig,
} from '../../projects/project.reducer';
import { Organisation } from '../../organisation/organisation.interface';
import {
  loadOrganisationById,
  setCurrentOrganisationID,
} from '../../organisation/organisation.actions';
import { organisationById } from '../../organisation/organisation.selector';
import {
  isProjectLoaded,
  projectLoadError,
  selectCurrentFileID,
  selectFirstScene,
} from '../../projects/project.selector';
import { selectShowAIPanel } from '../ai/store/ai.selector';
import { toggleAiPanel } from '../ai/store/ai.actions';
import { ResourceData } from '../../elements/resource/resource.types';
import { IRIToId } from '../../util/iri';
import { EditorService } from '../editor.service';

@Component({
  selector: 'iw-editor',
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.scss'],
})
export class EditorComponent implements OnInit, OnDestroy {
  ngOnDestroy() {
    Object.values(this.subscriptions).forEach(sub => sub.unsubscribe());
  }
  x: number;
  y: number;
  clicked: Point;
  menu = false;
  options: string[] = [];
  selected = 0;
  svgDisabled = false;
  readonly projectState$: Observable<'error' | 'loading' | 'loaded'>;
  readonly organisationId$: Observable<string>;
  readonly showAIPanel$: Observable<boolean>;
  readonly organisation$: Observable<Organisation>;
  readonly currentFileID$: Observable<string>;
  elements$: Observable<string[]>;
  loadedScene: string;
  isFileLoading$: Observable<boolean>;
  newFileId: string;

  get overlayConfig() {
    return this.service.overlayConfig;
  }

  set overlayConfig(config: OverlayConfig) {
    this.service.overlayConfig = config;
  }

  settingsMode = false;

  firstScene: ResourceData;
  get currentFileID() {
    return this.cs.currentFileID;
  }

  subscriptions: Record<string, Subscription> = {};

  @ViewChild('editorContainer') editorContainer: ElementRef;
  constructor(
    readonly cs: CanvasService,
    readonly shapeService: ShapeService,
    khs: KeyHandlerService,
    readonly http: HttpService,
    readonly dialog: MatDialog,
    private readonly store: Store,
    private readonly route: ActivatedRoute,
    private readonly service: EditorService,
  ) {
    // this.isFileLoading$ = this.store.select(isFileLoading);

    this.showAIPanel$ = this.store.select(selectShowAIPanel);

    this.currentFileID$ = this.store.select(selectCurrentFileID);

    this.cs.keyEventSubscribe('f+Shift', () => {
      this.overlayConfig = {
        type: 'color-selector',
        callback: value => {
          console.log('add-fill', value);
          this.store.dispatch(
            setDescriptorValue({
              key: 'fill',
              innerKey: 'color',
              value,
            }),
          );
          this.store.dispatch(bulkUpdateFinish());
        },
      };
      this.cs.consumeKeyEvent('Shift');
    });

    this.cs.keyEventSubscribe('f+Backspace', () => {
      this.store.dispatch(
        setDescriptorValue({
          key: 'fill',
          value: undefined,
        }),
      );
      this.store.dispatch(bulkUpdateFinish());
    });

    this.cs.keyEventSubscribe('s+Shift', () => {
      this.overlayConfig = {
        type: 'color-selector',
        callback: value => {
          this.store.dispatch(
            setDescriptorValue({
              key: 'stroke',
              innerKey: 'color',
              value,
            }),
          );
          this.store.dispatch(bulkUpdateFinish());
        },
      };
    });

    this.cs.keyEventSubscribe('s+Backspace', () => {
      this.store.dispatch(
        setDescriptorValue({
          key: 'stroke',
          value: undefined,
        }),
      );
      this.store.dispatch(bulkUpdateFinish());
    });

    this.cs.generalEventSubscribe('set-overlay', (config: OverlayConfig) => {
      this.overlayConfig = config;
    });
    this.cs.keyEventSubscribe('s+ArrowUp', () => {
      this.store.dispatch(incrementStrokeWidth({ increment: 1 }));
      this.store.dispatch(bulkUpdateFinish());
      this.cs.consumeKeyEvent('ArrowUp');
    });
    this.cs.keyEventSubscribe('s+ArrowDown', () => {
      this.store.dispatch(incrementStrokeWidth({ increment: -1 }));
      this.store.dispatch(bulkUpdateFinish());
      this.cs.consumeKeyEvent('ArrowDown');
    });
    this.cs.keyEventSubscribe('s+ArrowRight', () => {
      this.store.dispatch(incrementStrokeWidth({ increment: 5 }));
      this.store.dispatch(bulkUpdateFinish());
      this.cs.consumeKeyEvent('ArrowRight');
    });
    this.cs.keyEventSubscribe('s+ArrowLeft', () => {
      this.store.dispatch(incrementStrokeWidth({ increment: -5 }));
      this.store.dispatch(bulkUpdateFinish());
      this.cs.consumeKeyEvent('ArrowLeft');
    });

    this.cs.generalEventSubscribe('globalClick', () => {
      // -- // this.overlayResponse(); // -- //
      this.store.dispatch(toggleAiPanel({ show: false }));
    });

    this.organisationId$ = this.route.params.pipe(
      map(({ organisationID }) => organisationID),
      filter(id => !!id),
    );

    this.organisation$ = this.organisationId$.pipe(
      distinctUntilChanged(),
      tap(id => {
        if (id && this.cs.currentOrganisationID !== id) {
          this.cs.currentOrganisationID = id;
          this.store.dispatch(loadOrganisationById({ id }));
        }
        this.store.dispatch(setCurrentOrganisationID({ ID: id }));
      }),
      mergeMap(id => this.store.select(organisationById(id))),
    );

    this.organisation$.subscribe(org => {
      // console.log('Organisation data:', org); //
    });

    this.subscriptions['firstScene'] = this.store
      .select(selectFirstScene)
      .subscribe(scene => {
        // console.log('firstScene', scene); //
        this.firstScene = scene;
      });
    this.subscriptions['projectLoaded'] = this.store
      .select(isProjectLoaded)
      .subscribe(isLoaded => {
        if (isLoaded && !this.cs.currentFileID && this.firstScene) {
          console.log('open first scene', this.firstScene);
          this.shapeService.navigateToFile(IRIToId(this.firstScene.IRI));
        }
      });

    this.subscriptions['routeParams'] = this.route.params
      .pipe(filter(({ projectID }) => !!projectID))
      .subscribe(async ({ projectID, fileID }) => {
        // console.log('sub.routeParams', { projectID, fileID }); //
        if (projectID && projectID != this.cs.currentProjectID) {
          this.cs.store.dispatch(setCurrentProject({ id: projectID }));
          this.cs.store.dispatch(setProjectLoaded({ value: false }));
          this.cs.store.dispatch(loadProjectWithFilesById({ id: projectID }));
        }

        this.cs.currentProjectID = projectID;
        this.store.dispatch(setCurrentFileID({ id: fileID || '1' }));

        if (fileID && fileID !== this.cs.currentFileID) {
          console.log('open.file.after.route.params', fileID);
          this.shapeService.openFile(fileID);
        }

        if (!fileID) {
          this.shapeService.openFirstFile();
        }

        this.cs.currentRootFileID = fileID;
      });

    this.projectState$ = combineLatest([
      this.store.select(projectLoadError),
      this.store.select(isProjectLoaded),
    ]).pipe(
      map(([error, isLoaded]) => {
        if (error) {
          console.error('project-load-error', error);
          return 'error';
        }

        if (isLoaded) {
          return 'loaded';
        }

        return 'loading';
      }),
    );

    this.projectState$.subscribe(state => {
      // console.log('projectState', state); //
    });
  }

  setOverlayConfig(type: OverlayComponentType) {
    this.overlayConfig = { type };
  }
  overlayMode: 'set-fill' | 'set-stroke';

  overlayResponse(value?: unknown) {
    this.overlayConfig?.callback?.(value);
    // -- //
    this.overlayConfig = null;
    if (value == undefined) {
      this.overlayMode == null;
      return;
    }
  }

  ngOnInit() {
    this.x = 100;
    this.clicked = new Point(0, 0);
  }

  eventHandler(e: ComponentEvent) {
    switch (e.type) {
      case 'disableCanvas':
        this.svgDisabled = true;
        break;
      case 'enableCanvas':
        this.svgDisabled = false;
        break;
    }
  }

  openDialog() {
    this.dialog.open(FileUploaderComponent, {});
  }

  get showBack() {
    return !!this.shapeService.currentFile.relationships.parent;
  }

  moveBack() {
    this.shapeService.goBack();
  }
}
